import { PIM_WORKFLOW_STATE, type PimWorkflowReceipt, type PimWorkflowScope } from "@nova-ai/shared"

export const WORKFLOW_STAGES = [
  ["dispatch", "Dispatch"],
  ["content", "Content"],
  ["generation", "Generated"],
  ["upload", "Uploaded"],
  ["current", "Made current"],
  ["publication", "Published"],
] as const

export const SOURCE_KIND_OPTIONS = [
  { value: "nutritional_values", label: "Nutrition" },
  { value: "amino_acid_profile", label: "Amino acids" },
  { value: "size_guide", label: "Size guide" },
]

export function workflowKindLabel(kind: string | null) {
  if (!kind) {
    return "Kind unresolved"
  }
  if (kind === "nutrition") {
    return "Nutrition"
  }
  if (kind === "amino") {
    return "Amino acids"
  }
  return (
    SOURCE_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? `Unknown kind (${kind})`
  )
}

const REASON_COPY: Record<string, string> = {
  generation_paused_observed_now: "Generation paused (observed now)",
  dispatch_paused_observed_now: "Publication paused (observed now)",
  pubsub_accepted: "Pub/Sub accepted",
  withdrawal_accepted: "Withdrawal accepted",
  attempt_failed: "The attempt failed. No specific cause was recorded.",
  publication_statement_timeout: "Making the image current exceeded the database time limit.",
  publication_lock_timeout: "Making the image current could not acquire the database lock in time.",
  publication_failed: "Publication failed.",
  publication_outcome_unknown: "The outcome of making the image current could not be confirmed.",
  publication_restore_failed:
    "Re-uploading the image object for publication failed. Investigation is required.",
  current_write_failed: "Current write failed",
  publisher_failed: "Publication failed",
  render_failed: "Image rendering failed",
  source_unavailable: "Source unavailable",
  active_store_conflict: "Another active store owns the conflicting work",
  scope_unavailable: "The target scope is unavailable",
  fill_failed: "Content preparation failed",
  evidence_expired: "Recorded evidence expired",
  capture_disabled: "Publication capture was disabled",
  no_change: "No change",
  newer_revision: "Newer revision",
  unsupported: "Unsupported",
  superseded: "Superseded",
  withdrawal: "Image withdrawn",
  gap: "Required source content is missing",
  excluded: "Excluded from image generation",
  disabled: "Disabled",
  target_not_terminal: "Target is not a terminal dispatch failure",
  validation_failed: "The source event did not pass validation",
  "Pub/Sub envelope did not match the required contract": "Invalid Pub/Sub envelope",
  "decoded PIM event did not match the required contract": "Invalid PIM event",
  "PIM event body and Pub/Sub attributes disagree": "PIM event and transport attributes disagree",
  "decoded PIM event exceeds 64 KiB": "PIM event exceeds the size limit",
  "PIM event data is not valid base64": "PIM event encoding is invalid",
  "PIM event data is not valid base64 JSON": "PIM event data is invalid",
}

export function workflowReason(reason: string | null) {
  if (!reason) {
    return "Reason not recorded."
  }
  return Object.hasOwn(REASON_COPY, reason)
    ? REASON_COPY[reason]
    : "No safe explanation is available for the recorded cause."
}

export function workflowCoverage(coverage: PimWorkflowReceipt["coverage"]) {
  if (coverage === "expired") {
    return "History expired: some retained evidence was explicitly removed. Known stages remain visible."
  }
  return "Partial tracing coverage. Some lifecycle links were not captured. Recorded stages remain valid."
}

export function scopeFailures(scope: PimWorkflowScope) {
  return WORKFLOW_STAGES.filter(
    ([key]) =>
      scope[key].state === PIM_WORKFLOW_STATE.failed &&
      (key === "publication" || !scope.recovery?.resolvedAt)
  ).map(([key, label]) => ({ label, reason: workflowReason(scope[key].reason) }))
}

export function groupWorkflowScopes(scopes: PimWorkflowScope[]) {
  const groups = new Map<string, PimWorkflowScope[]>()
  for (const scope of scopes) {
    // A receipt owns the product. Only store and recorded times may differ within a group.
    // Unresolved SKUs cannot establish shared product identity, so keep them separate.
    const key = JSON.stringify([
      scope.sku ?? scope.id,
      scope.kind,
      Boolean(scope.recovery?.resolvedAt),
      ...WORKFLOW_STAGES.map(([stage]) => [
        scope[stage].state,
        scope[stage].reason,
        scope[stage].evidence,
      ]),
    ])
    const members = groups.get(key) ?? []
    members.push(scope)
    groups.set(key, members)
  }
  return Array.from(groups, ([key, members]) => ({ key, members }))
}

export function hasNoDownstreamHistory(scope: PimWorkflowScope) {
  return WORKFLOW_STAGES.filter(([key]) => key !== "dispatch").every(([key]) => {
    const stage = scope[key]
    return (
      stage.state === PIM_WORKFLOW_STATE.unknown &&
      stage.evidence !== "expired" &&
      !stage.reason &&
      !stage.recordedAt
    )
  })
}

export function receiptOutcome(receipt: PimWorkflowReceipt) {
  if (receipt.validation === "rejected") {
    return { label: "Receipt rejected", tone: "destructive" } as const
  }
  if (receipt.summary.failed) {
    return { label: "Unresolved failure in this receipt", tone: "destructive" } as const
  }
  if (receipt.summary.pending) {
    return { label: "Work is still pending", tone: "info" } as const
  }
  const publication = receipt.summary.publication
  if (
    !receipt.summary.unknown &&
    publication.complete > 0 &&
    publication.complete + publication.notApplicable === publication.total
  ) {
    // This also includes withdrawals. Never describe these as newly published images.
    return { label: "All publications accepted", tone: "success" } as const
  }
  if (receipt.coverage === "expired") {
    return { label: "Some workflow evidence has expired", tone: "muted" } as const
  }
  if (receipt.summary.unknown || receipt.coverage === "partial") {
    return { label: "Workflow history is incomplete", tone: "muted" } as const
  }
  if (publication.total > 0 && publication.notApplicable === publication.total) {
    return { label: "No publication required for the recorded work", tone: "muted" } as const
  }
  return { label: "No publication outcome recorded", tone: "muted" } as const
}
